import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["childNodeIds"];
import { immerable } from 'immer';
import GridNode from './GridNode';
import { CONTAINER_VARIANTS, DEFAULT_BREAKPOINT_NAME, GRID_NODE_TYPES } from './constants';
import { isPegboardContainerLayout } from './typenameUtils';
import { iterateFrom, mergeIntoPath } from './utils';
import { ContainerTypename } from '../../__generated__/chirp/com/hubspot/content/grid/layouts/rpc/GridLayoutsRpc';
export default class PegboardContainerNode extends GridNode {
  constructor(_ref) {
    let {
        childNodeIds = []
      } = _ref,
      containerNodeParams = _objectWithoutPropertiesLoose(_ref, _excluded);
    super(containerNodeParams);
    this[immerable] = true;
    this.containerVariant = CONTAINER_VARIANTS.PEGBOARD;
    this._childNodeIds = childNodeIds;
  }
  cloneAndChange(containerNodeParams) {
    const {
      id,
      childNodeIds,
      parentNodeId,
      value,
      treeRef
    } = containerNodeParams;
    const newNodeParams = {
      id: id !== null && id !== void 0 ? id : this._id,
      type: GRID_NODE_TYPES.CONTAINER,
      treeRef: treeRef !== null && treeRef !== void 0 ? treeRef : this._treeRef,
      childNodeIds: childNodeIds !== null && childNodeIds !== void 0 ? childNodeIds : this._childNodeIds,
      parentNodeId: parentNodeId !== null && parentNodeId !== void 0 ? parentNodeId : this._parentNodeId,
      value: value !== null && value !== void 0 ? value : this._value,
      variant: this.containerVariant
    };
    return newNodeParams.treeRef.createNewContainer(newNodeParams);
  }
  serializeNode(options) {
    return Object.assign({}, this.getValue(), {
      children: this.getChildNodes().map(child => child.serializeNode(options)),
      __typename: ContainerTypename
    });
  }
  exportNode() {
    return {
      id: this.getId(),
      value: this.getValue(),
      parentNodeId: this.getParentNodeId(),
      childNodeIds: [...this.getChildNodeIds()]
    };
  }
  getChildNodes() {
    return this.getChildNodeIds().map(childNodeId => this.getTree().findNode(childNodeId));
  }
  getVisibleChildNodes(breakpoint = DEFAULT_BREAKPOINT_NAME) {
    return this.getChildNodes().filter(child => !child.getEditorProvidedBreakpointStyles(breakpoint).isHidden);
  }
  getChildBoundingBoxes(breakpoint = DEFAULT_BREAKPOINT_NAME) {
    return this.getChildNodes().map(child => this.getTree().getBoundingBoxOfPegboardNode(child, breakpoint)).filter(bbox => bbox !== null);
  }
  mergeIntoValue(partialValue, path) {
    return this.cloneAndChange({
      value: mergeIntoPath(this._value, partialValue, path)
    });
  }
  mergeIntoStylesByPath(partialStyles, path) {
    return this.cloneAndChange({
      value: Object.assign({}, this._value, {
        styles: mergeIntoPath(this.getStyles(), partialStyles, path)
      })
    });
  }
  mergeIntoStyles(partialStyles) {
    return this.cloneAndChange({
      value: Object.assign({}, this._value, {
        styles: mergeIntoPath(this.getStyles(), partialStyles, ['breakpointStyles'])
      })
    });
  }
  isChildOfSection() {
    return this.getParentNode().isSection();
  }
  getLayout() {
    return this.getValue().layout;
  }
  getContainerLayout(breakpoint = 'default') {
    const layout = this.getLayout().layout[breakpoint] || this.getLayout().layout.default;
    if (!isPegboardContainerLayout(layout)) {
      throw new Error(`Expected a PegboardContainerLayout at breakpoint "${breakpoint}" but got "${layout.__typename}".`);
    }
    return layout;
  }
  isGrid(__breakpoint = 'default') {
    return false;
  }
  isStack(__breakpoint = 'default') {
    return false;
  }
  isHorizontalStack(__breakpoint = 'default') {
    return false;
  }
  isVerticalStack(__breakpoint = 'default') {
    return false;
  }
  getBreakpointStyles(breakpoint = 'default') {
    var _this$getStyles$break;
    return (_this$getStyles$break = this.getStyles().breakpointStyles[breakpoint]) !== null && _this$getStyles$break !== void 0 ? _this$getStyles$break : {
      backgroundLayers: []
    };
  }
  getStyles() {
    return this.getValue().styles;
  }
  getEffectiveStyle(style, breakpoint, breakpointsByInheritance) {
    var _containerBreakpointS;
    const containerBreakpointStyles = this.getStyles().breakpointStyles;
    const styleAtBreakpoint = (_containerBreakpointS = containerBreakpointStyles[breakpoint]) === null || _containerBreakpointS === void 0 ? void 0 : _containerBreakpointS[style];
    if (styleAtBreakpoint !== undefined) {
      return {
        value: styleAtBreakpoint,
        inheritedBy: breakpoint
      };
    }
    const parentBreakpoint = breakpointsByInheritance[breakpoint];
    if (parentBreakpoint === undefined) {
      var _containerBreakpointS2;
      return {
        value: (_containerBreakpointS2 = containerBreakpointStyles[DEFAULT_BREAKPOINT_NAME]) === null || _containerBreakpointS2 === void 0 ? void 0 : _containerBreakpointS2[style],
        inheritedBy: DEFAULT_BREAKPOINT_NAME
      };
    }
    return this.getEffectiveStyle(style, parentBreakpoint, breakpointsByInheritance);
  }
  getEffectiveContainerLayout(breakpoint, breakpointsByInheritance) {
    const containerLayout = this.getLayout().layout;
    const layoutAtBreakpoint = containerLayout[breakpoint];
    const parentBreakpoint = breakpointsByInheritance[breakpoint];
    if (!layoutAtBreakpoint && parentBreakpoint) {
      return this.getEffectiveContainerLayout(parentBreakpoint, breakpointsByInheritance);
    }
    const inheritedBy = layoutAtBreakpoint ? breakpoint : DEFAULT_BREAKPOINT_NAME;
    const value = layoutAtBreakpoint !== null && layoutAtBreakpoint !== void 0 ? layoutAtBreakpoint : containerLayout[DEFAULT_BREAKPOINT_NAME];
    if (!isPegboardContainerLayout(value)) {
      throw new Error(`Expected a PegboardContainerLayout at breakpoint "${inheritedBy}" but got "${value.__typename}".`);
    }
    return {
      value,
      inheritedBy
    };
  }
  getLabel() {
    var _this$getValue$label;
    return (_this$getValue$label = this.getValue().label) !== null && _this$getValue$label !== void 0 ? _this$getValue$label : '';
  }
  getAllDescendantNodeIds() {
    const descendantNodeIds = [];
    iterateFrom(this, {
      includeStartNode: false,
      gridNodeCallback: ({
        gridNode
      }) => {
        descendantNodeIds.push(gridNode.getId());
      }
    });
    return descendantNodeIds;
  }
  getMaxChildZIndex(breakpointName = DEFAULT_BREAKPOINT_NAME, breakpointsByInheritance = {}) {
    let maxZIndex = 0;
    for (const child of this.getChildNodes()) {
      const {
        value: zIndex
      } = child.getEffectiveEditorProvidedStyle('zIndex', breakpointName, breakpointsByInheritance);
      if ((zIndex !== null && zIndex !== void 0 ? zIndex : 0) > maxZIndex) {
        maxZIndex = zIndex !== null && zIndex !== void 0 ? zIndex : 0;
      }
    }
    return maxZIndex;
  }
  generateZIndex(breakpointName = DEFAULT_BREAKPOINT_NAME, breakpointsByInheritance = {}) {
    return this.getMaxChildZIndex(breakpointName, breakpointsByInheritance) + 1;
  }
  getMinRowCount(breakpoint = DEFAULT_BREAKPOINT_NAME) {
    let minRowCount = 1;
    let bottomMostModuleNodes = [];
    const tree = this.getTree();
    for (const child of this.getChildNodes()) {
      const boundingBox = tree.getBoundingBoxOfPegboardNode(child, breakpoint);
      if (!boundingBox) continue;
      const {
        rowBottom
      } = boundingBox;
      if (rowBottom > minRowCount) {
        minRowCount = rowBottom;
        bottomMostModuleNodes = [child];
      } else if (rowBottom === minRowCount) {
        bottomMostModuleNodes.push(child);
      }
    }
    return {
      minRowCount,
      bottomMostModuleNodes
    };
  }
}